import clsx from 'clsx';
import {
  Collapsible,
  CollapsibleChevron,
  CollapsiblePanel,
  CollapsibleTrigger
} from '../Collapsible/index.ts';

export function StackedLayout({
  children,
  divided = true,
  size = 'md'
}: React.PropsWithChildren<{
  divided?: boolean;
  size?: 'sm' | 'md';
}>) {
  return (
    <div
      data-size={size}
      className={clsx(
        'group/stacked-layout',
        'data-[size=sm]:-mt-6',
        'data-[size=md]:-mt-8',
        divided && 'divide-y divide-gray-200'
      )}
    >
      {children}
    </div>
  );
}

export function StackedLayoutSection({
  children,
  collapsible = false,
  description,
  heading,
  initialOpen = false
}: React.PropsWithChildren<
  {
    description?: React.ReactNode;
    initialOpen?: boolean;
  } & (
    | { collapsible: true; heading: React.ReactNode }
    | { collapsible?: false; heading?: React.ReactNode }
  )
>) {
  const headingMarkup = (heading || collapsible) && (
    <h3
      className={clsx(
        'leading-6 font-medium text-gray-900',
        'group-data-[size=sm]/stacked-layout:text-base',
        'group-data-[size=md]/stacked-layout:text-lg'
      )}
    >
      {collapsible ? (
        <CollapsibleTrigger className="flex w-full items-center justify-between text-left">
          <span className="grow">{heading}</span>
          <CollapsibleChevron
            className={clsx(
              'group-data-[size=sm]/stacked-layout:size-5',
              'group-data-[size=md]/stacked-layout:size-6'
            )}
          />
        </CollapsibleTrigger>
      ) : (
        heading
      )}
    </h3>
  );

  const body = (
    <>
      {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
      <div className={clsx(heading && 'mt-6')}>{children}</div>
    </>
  );

  if (!collapsible) {
    return (
      <div
        className={clsx(
          'group-data-[size=sm]/stacked-layout:py-6',
          'group-data-[size=md]/stacked-layout:py-8'
        )}
      >
        {headingMarkup}
        {body}
      </div>
    );
  }

  return (
    <Collapsible
      className={clsx(
        'group-data-[size=sm]/stacked-layout:py-6',
        'group-data-[size=md]/stacked-layout:py-8'
      )}
      defaultOpen={initialOpen}
    >
      {headingMarkup}

      <CollapsiblePanel className="group/stacked-layout-panel">{body}</CollapsiblePanel>
    </Collapsible>
  );
}
